/* The queue filter, in the shape the rest of Shaip Hub uses.
 *
 * A "Filter (N)" button opens a popover with the filter NAMES down the left and
 * the selected filter's CONTROL on the right, over one Clear All / Cancel /
 * Apply footer. That is the platform's existing pattern, so an annotator who
 * has used any other Shaip screen already knows this one.
 *
 * IT REPLACES THE CHIP ROW. 27 sub-categories rendered as chips took seven
 * lines and pushed the table below the fold — the original complaint. Clamping
 * them to two lines behind a More button treated the symptom; the real problem
 * is that a flat list of every value is not a filter UI once the corpus is more
 * than a page long, and the corpus is 190 videos across 28 sub-categories.
 *
 * CHANGES ARE STAGED, NOT LIVE. Ticking a box edits a draft; only Apply commits
 * it, and Cancel throws the draft away. That is what makes the footer honest —
 * a panel with Cancel and Apply buttons that had already changed the table
 * behind it would be lying about both.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { VideoRow } from "../api/types";
import { clock, videoName } from "../lib/format";

/** Empty means "no constraint", never "match nothing" — a filter nobody has
 *  touched must not hide rows. Every predicate below short-circuits on empty. */
export interface Filters {
  categories: string[];
  subCategories: string[];
  statuses: string[];
  sessions: string[];
  minLength: string;
  maxLength: string;
  minSubtasks: string;
  maxSubtasks: string;
}

export const EMPTY_FILTERS: Filters = {
  categories: [], subCategories: [], statuses: [], sessions: [],
  minLength: "", maxLength: "", minSubtasks: "", maxSubtasks: "",
};

/** The status vocabulary the rows show. Kept as one list so the filter and the
 *  pill cannot drift — a filter offering a status no row can have is worse than
 *  no filter, because it returns nothing and looks broken. */
export const STATUS_OPTIONS = [
  "Ready for review", "In progress", "Submitted to QA", "Approved", "Rejected",
] as const;

export function statusOf(v: VideoRow): string {
  if (v.current_stage === "AI") return "Ready for review";
  if (v.current_stage === "ANNOTATOR")
    return v.current_status === "COMPLETED" ? "Submitted to QA" : "In progress";
  if (v.current_stage === "QA")
    return v.current_status === "REJECTED" ? "Rejected" : "Submitted to QA";
  return "Approved";
}

export const activeCount = (f: Filters): number =>
  f.categories.length + f.subCategories.length + f.statuses.length
  + f.sessions.length
  + (f.minLength ? 1 : 0) + (f.maxLength ? 1 : 0)
  + (f.minSubtasks ? 1 : 0) + (f.maxSubtasks ? 1 : 0);

export function applyFilters(rows: VideoRow[], f: Filters): VideoRow[] {
  const num = (s: string) => (s.trim() === "" ? null : Number(s));
  const minL = num(f.minLength), maxL = num(f.maxLength);
  const minS = num(f.minSubtasks), maxS = num(f.maxSubtasks);
  return rows.filter((v) => {
    if (f.categories.length && !f.categories.includes(v.category ?? "")) return false;
    if (f.subCategories.length && !f.subCategories.includes(v.sub_category ?? "")) return false;
    if (f.statuses.length && !f.statuses.includes(statusOf(v))) return false;
    if (f.sessions.length && !f.sessions.includes(v.session_code ?? "")) return false;
    const secs = v.duration_seconds ?? 0;
    // Length is typed in SECONDS but read as a clock in the hint, because the
    // column shows 03:00 and asking someone to convert is how a filter gets a
    // reputation for not working.
    if (minL !== null && !Number.isNaN(minL) && secs < minL) return false;
    if (maxL !== null && !Number.isNaN(maxL) && secs > maxL) return false;
    if (minS !== null && !Number.isNaN(minS) && v.subtask_count < minS) return false;
    if (maxS !== null && !Number.isNaN(maxS) && v.subtask_count > maxS) return false;
    return true;
  });
}

type PaneId = "category" | "subCategory" | "status" | "length" | "subtasks" | "session";
const PANES: { id: PaneId; label: string }[] = [
  { id: "category", label: "Category" },
  { id: "subCategory", label: "Sub-category" },
  { id: "status", label: "Status" },
  { id: "length", label: "Video length" },
  { id: "subtasks", label: "Sub-tasks" },
  { id: "session", label: "Session" },
];

export function FilterPanel(
  { rows, applied, onApply }:
  { rows: VideoRow[]; applied: Filters; onApply: (f: Filters) => void },
) {
  const [open, setOpen] = useState(false);
  const [pane, setPane] = useState<PaneId>("category");
  const [draft, setDraft] = useState<Filters>(applied);
  const [search, setSearch] = useState("");
  const box = useRef<HTMLDivElement>(null);

  // Opening always starts from what is currently applied, never from the last
  // abandoned draft — Cancel has to mean the panel forgets.
  useEffect(() => { if (open) { setDraft(applied); setSearch(""); } }, [open, applied]);

  /** How tall the popover may be, measured against the space actually below the
   *  button.
   *
   *  A FIXED HEIGHT IS NOT ENOUGH AND CAPPING THE LIST WAS NOT EITHER. The list
   *  stops at five rows, but the panel is still ~365px and it opens ~419px down
   *  the page, so on a 1366×768 laptop its bottom lands at 784 — Apply and
   *  Cancel are below the fold, on a popover that traps the click-away, and the
   *  filter cannot be committed at all. Measured on 1366×768 and 1280×720.
   *
   *  CSS cannot express this: the limit depends on where the button ended up,
   *  which depends on the stats strip above it. So it is measured, on open and
   *  on resize, and the panes shrink into whatever is left. */
  const [maxH, setMaxH] = useState<number | null>(null);
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const r = box.current?.getBoundingClientRect();
      if (!r) return;
      // 16px of breathing room below, and never so small it stops being a panel.
      setMaxH(Math.max(240, window.innerHeight - r.bottom - 16));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  // Options come from the rows on screen, so a value that cannot match anything
  // is never offered. Counts are what make a long list decidable at a glance.
  const options = useMemo(() => {
    const tally = (pick: (v: VideoRow) => string) => {
      const m = new Map<string, number>();
      for (const v of rows) {
        const k = pick(v);
        if (k) m.set(k, (m.get(k) ?? 0) + 1);
      }
      return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    };
    return {
      category: tally((v) => v.category ?? ""),
      subCategory: tally((v) => v.sub_category ?? ""),
      status: tally(statusOf),
      session: tally((v) => v.session_code ?? ""),
    };
  }, [rows]);

  const n = activeCount(applied);

  const toggle = (key: keyof Filters, value: string) => setDraft((d) => {
    const cur = d[key] as string[];
    return { ...d, [key]: cur.includes(value)
      ? cur.filter((x) => x !== value) : [...cur, value] };
  });

  return (
    <div className="relative" ref={box}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`flex items-center gap-2 rounded-control border px-3 py-1.5 text-sm
          ${n ? "border-teal bg-tealbg text-teal7" : "border-line bg-card text-text2 hover:bg-ground"}`}
      >
        {/* A FUNNEL, not "⛃". That glyph is U+26C3 BLACK DRAUGHTS KING — a
            checkers piece. It was standing in for a filter icon and renders as
            a different shape in every font that has it at all, which is why
            testing asked for "the filter icon like in SC 2.0". An inline SVG
            has no font dependency and says funnel everywhere. */}
        <svg aria-hidden viewBox="0 0 16 16" className="h-3.5 w-3.5"
          fill="none" stroke="currentColor" strokeWidth="1.6"
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M1.5 2.5h13l-5 5.6v4.4l-3 1.5V8.1z" />
        </svg>
        Filter
        <span className={`rounded-full px-1.5 text-xs font-semibold
          ${n ? "bg-teal text-white" : "bg-ground text-muted"}`}>{n}</span>
      </button>

      {open && (
        <div
          style={maxH ? { maxHeight: maxH } : undefined}
          className="absolute left-0 z-40 mt-1 flex w-[46rem] max-w-[92vw]
            flex-col overflow-hidden rounded-panel border border-line bg-card
            shadow-raised">
          {/* basis-, NOT h-. 19rem is the PREFERRED height — the same on every
              pane, so the panel does not resize as you move between them — but
              `min-h-0` and the default shrink let it give way when the popover
              is capped by the viewport above. A hard `h-[19rem]` would overflow
              the cap instead of yielding, and the footer would go off screen
              exactly as it did before. */}
          <div className="flex min-h-0 flex-1 basis-[19rem]">
            {/* the rail */}
            <ul className="w-48 shrink-0 overflow-y-auto border-r border-line py-1">
              {PANES.map((x) => (
                <li key={x.id}>
                  <button
                    onClick={() => { setPane(x.id); setSearch(""); }}
                    className={`w-full px-4 py-2.5 text-left text-sm
                      ${pane === x.id
                        ? "bg-tealbg font-semibold text-ink"
                        : "text-text2 hover:bg-ground"}`}
                  >
                    {x.label}
                  </button>
                </li>
              ))}
            </ul>

            {/* the pane */}
            <div className="min-w-0 flex-1 overflow-y-auto p-4">
              {pane === "category" && (
                <CheckList options={options.category} chosen={draft.categories}
                  onToggle={(v) => toggle("categories", v)} />
              )}
              {pane === "subCategory" && (
                <CheckList options={options.subCategory} chosen={draft.subCategories}
                  onToggle={(v) => toggle("subCategories", v)}
                  search={search} onSearch={setSearch} />
              )}
              {pane === "status" && (
                <CheckList options={options.status} chosen={draft.statuses}
                  onToggle={(v) => toggle("statuses", v)} />
              )}
              {pane === "session" && (
                <CheckList options={options.session} chosen={draft.sessions}
                  onToggle={(v) => toggle("sessions", v)}
                  search={search} onSearch={setSearch} />
              )}
              {pane === "length" && (
                <Range
                  minLabel="Min (seconds)" maxLabel="Max (seconds)"
                  min={draft.minLength} max={draft.maxLength}
                  onMin={(v) => setDraft((d) => ({ ...d, minLength: v }))}
                  onMax={(v) => setDraft((d) => ({ ...d, maxLength: v }))}
                  hint={(a, b) => `${clock(Number(a || 0))} – ${b ? clock(Number(b)) : "any"}`}
                />
              )}
              {pane === "subtasks" && (
                <Range
                  minLabel="Min" maxLabel="Max"
                  min={draft.minSubtasks} max={draft.maxSubtasks}
                  onMin={(v) => setDraft((d) => ({ ...d, minSubtasks: v }))}
                  onMax={(v) => setDraft((d) => ({ ...d, maxSubtasks: v }))}
                />
              )}
            </div>
          </div>

          {/* Live count, because Apply is a commitment and the panel should say
              what it commits to before it is pressed. */}
          <footer className="flex items-center gap-2 border-t border-line px-4 py-3">
            <button
              onClick={() => setDraft(EMPTY_FILTERS)}
              className="rounded-control border border-line bg-card px-4 py-1.5 text-sm
                text-text2 hover:bg-ground"
            >Clear All</button>
            <span className="text-xs text-muted">
              {applyFilters(rows, draft).length} of {rows.length} videos
            </span>
            <button
              onClick={() => setOpen(false)}
              className="ml-auto rounded-control bg-tealbg px-4 py-1.5 text-sm
                font-medium text-teal7 hover:bg-teal1"
            >Cancel</button>
            <button
              onClick={() => { onApply(draft); setOpen(false); }}
              className="rounded-control bg-navy9 px-5 py-1.5 text-sm font-medium
                text-white hover:opacity-90"
            >Apply</button>
          </footer>
        </div>
      )}
    </div>
  );
}

function CheckList(
  { options, chosen, onToggle, search, onSearch }: {
    options: [string, number][]; chosen: string[]; onToggle: (v: string) => void;
    search?: string; onSearch?: (v: string) => void;
  },
) {
  const q = (search ?? "").trim().toLowerCase();
  const shown = q ? options.filter(([k]) => k.toLowerCase().includes(q)) : options;
  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      {/* The search box does NOT scroll with the list. It is how you cope with
          a list too long to read, so scrolling it away is exactly backwards. */}
      {onSearch && (
        <label className="flex shrink-0 items-center gap-2 border-b border-line pb-2">
          <span className="text-muted" aria-hidden>⌕</span>
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search"
            className="w-full bg-transparent text-sm outline-none placeholder:text-faint"
          />
        </label>
      )}
      {shown.length === 0 && (
        <p className="py-6 text-center text-sm text-faint">Nothing matches that.</p>
      )}
      {/* PAST FIVE ITEMS THE LIST SCROLLS — 2.375rem a row, so max-h-[11.875rem]
          is exactly five and a sixth is visibly cut off rather than hidden. A
          list that ends flush with the panel looks complete when it is not, and
          nothing else on screen says there is more. */}
      <div className="min-h-0 flex-1 overflow-y-auto pr-1"
        style={{ maxHeight: "11.875rem" }}>
        {shown.map(([value, count]) => (
          <label key={value}
            className="flex h-[2.375rem] cursor-pointer items-center gap-3 rounded-chip
              px-1 text-sm text-ink hover:bg-ground">
            <input
              type="checkbox"
              checked={chosen.includes(value)}
              onChange={() => onToggle(value)}
              className="size-4 accent-teal"
            />
            <span className="min-w-0 flex-1 truncate">{value}</span>
            <span className="shrink-0 text-xs text-faint">{count}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function Range(
  { minLabel, maxLabel, min, max, onMin, onMax, hint }: {
    minLabel: string; maxLabel: string; min: string; max: string;
    onMin: (v: string) => void; onMax: (v: string) => void;
    hint?: (min: string, max: string) => string;
  },
) {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-4">
        {([[minLabel, min, onMin], [maxLabel, max, onMax]] as const).map(
          ([label, value, set]) => (
            <label key={label} className="flex flex-col gap-1">
              <span className="text-[11px] text-muted">{label}</span>
              <input
                type="number"
                min={0}
                value={value}
                onChange={(e) => set(e.target.value)}
                className="border-b border-line bg-transparent px-1 py-1.5 text-sm
                  text-ink outline-none focus:border-teal"
              />
            </label>
          ))}
      </div>
      {/* Seconds are what you type; a clock is what the column shows. Echoing
          the range back as a clock is cheaper than making people convert. */}
      {hint && (min || max) && (
        <p className="text-xs text-muted">{hint(min, max)}</p>
      )}
    </div>
  );
}

/** The applied filters, as removable chips under the button — otherwise the
 *  only sign that a filter is on is a number on a button, and a short table
 *  reads as an empty corpus. */
export function ActiveFilterChips(
  { filters, onChange }: { filters: Filters; onChange: (f: Filters) => void },
) {
  const drop = (key: keyof Filters, value: string) => onChange({
    ...filters,
    [key]: Array.isArray(filters[key])
      ? (filters[key] as string[]).filter((x) => x !== value) : "",
  });

  const chips: { key: keyof Filters; label: string; value: string }[] = [
    ...filters.categories.map((v) => ({ key: "categories" as const, label: v, value: v })),
    ...filters.subCategories.map((v) => ({ key: "subCategories" as const, label: v, value: v })),
    ...filters.statuses.map((v) => ({ key: "statuses" as const, label: v, value: v })),
    ...filters.sessions.map((v) => ({ key: "sessions" as const, label: v, value: v })),
  ];
  if (filters.minLength) chips.push({ key: "minLength", label: `≥ ${clock(Number(filters.minLength))}`, value: "" });
  if (filters.maxLength) chips.push({ key: "maxLength", label: `≤ ${clock(Number(filters.maxLength))}`, value: "" });
  if (filters.minSubtasks) chips.push({ key: "minSubtasks", label: `≥ ${filters.minSubtasks} sub-tasks`, value: "" });
  if (filters.maxSubtasks) chips.push({ key: "maxSubtasks", label: `≤ ${filters.maxSubtasks} sub-tasks`, value: "" });

  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map((c) => (
        <button
          key={`${c.key}:${c.label}`}
          onClick={() => drop(c.key, c.value)}
          title="Remove this filter"
          className="flex items-center gap-1.5 rounded-full bg-tealbg px-3 py-1
            text-xs text-teal7 ring-1 ring-teal1 hover:bg-teal1"
        >
          {c.label}<span aria-hidden>✕</span>
        </button>
      ))}
      <button
        onClick={() => onChange(EMPTY_FILTERS)}
        className="text-xs text-muted underline hover:text-text2"
      >Clear all</button>
    </div>
  );
}

export { videoName };
